import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useTheme, type ThemeSetting } from "@/lib/theme";

export function ThemeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { themeSetting, setThemeSetting } = useTheme();
  const options: { value: ThemeSetting; label: string; icon: typeof Sun }[] = [
    { value: "light", label: t("theme.light", "Light"), icon: Sun },
    { value: "dark", label: t("theme.dark", "Dark"), icon: Moon },
    { value: "system", label: t("theme.system", "System"), icon: Monitor },
  ];

  return (
    <div
      className={cn("inline-flex items-center rounded-full border border-ink/25 bg-panel p-0.5", className)}
      role="group"
      aria-label={t("theme.selectAria", "Select color theme")}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setThemeSetting(value)}
          className={cn(
            "inline-flex size-7 items-center justify-center rounded-full border border-transparent text-muted transition-colors hover:text-ink focus-visible:border-focus focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus pointer-coarse:size-11",
            themeSetting === value && "bg-ink text-canvas hover:text-canvas",
          )}
          aria-label={label}
          aria-pressed={themeSetting === value}
        >
          <Icon className="size-3.5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
